<template>
	<div class="provider-timeline">
		<ProviderIcon
			:providerShortName="providerShortName"
			:iconUrl="providerIcon"
			class="provider-timeline__logo"
			size="small"
			:lazy="!!lazyLoadImages"
		/>
		{{ titleCountLabel }}

		<HorizontalTitleList
			class="provider-timeline__titles"
			:titles="providerTitles"
			:maxTitles="titleCount ?? 0"
			:isBlocking="!isFirstPageLoaded && loading"
			:language="language"
			:country="country"
			:showPriceDrop="isPriceDropsTimeline"
			clickSource="new"
			:key="`timeline--${timelineDate}--${providerIndex}--${bucketType ? bucketType : 'date'}`"
			@onPosterClick="onPosterClick"
			@onScrollRight="onLoadNext"
		>
			<template #poster="{ title, index, handlers }">
				<!-- v-bind="props" -->
				<PosterGraphql
					v-on="handlers"
					:title="title"
					:watchNowOffer="title.watchNowOffer"
					:showRibbon="!isLoggedIn"
					:showQuickActions="isLoggedIn && isMounted"
					:forceQuickActions="!isFirstPageLoaded"
					:showWatchNowButton="!isPriceDropsTimeline"
					:showTvBadge="true"
					:showNewEpisodesCount="title.watchNowOffer != null"
					clickSource="new"
					:lazy="index >= imagesLoadCount"
					:showAvailabilityBadge="true"
					availabilityBadgeSize="short"
					:noRadius="isPriceDropsTimeline ? 'bottom' : undefined"
				/>
			</template>
		</HorizontalTitleList>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import HorizontalTitleList from '@/components/timeline/HorizontalTitleList.vue';

import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { captureMessageForSentry } from '@/libraries/sentry';
import { toastError } from '@/helpers/vfm/toast-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers';

import { useFilterStore } from '@/stores/pinia/useFilterStore';

import type { GetNewTitlesQuery, GetNewTitlesQueryVariables } from '@/pages/graphql/queries/GetNewTitles.query';
import { GetNewTitlesDocument } from '@/pages/graphql/queries/GetNewTitles.query';

import { NewDateRangeBucket, NewPageType, ObjectType, Platform, TitleFilter } from '@/@types/graphql-types';
import { CollectionType } from '@/enums/collection-type';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useMounted } from '@vueuse/core';

const props = withDefaults(
	defineProps<{
		providerId: number;
		timelineDate: string;
		providerIcon: string;
		providerShortName: string;
		dayIndex?: number | null;
		titleCount?: number | null;
		providerIndex?: number | null;
		bucketType?: NewDateRangeBucket | null;
		additionalContexts?: SnowplowContext[];
	}>(),
	{
		providerShortName: '',
		additionalContexts: () => [],
	}
);

const emit = defineEmits(['onPosterClick', 'passUpTitles']);

const route = useRoute();
const { t } = useI18n();
const { language, country } = storeToRefs(useLanguageStore());
const { filters, selectedProviders } = useFilterStore();
const { isHideSeenDislikedTitlesEnabled, isLoggedIn } = storeToRefs(useUserStore());

const isMounted = useMounted();

const isFirstPageLoaded = ref(false);

const newPageType = computed(() => route.meta?.newPageType as NewPageType);
const isPriceDropsTimeline = computed(() => newPageType.value === NewPageType.PriceDrop);

const filtersNew = computed(() => filters(CollectionType.NEW));
const graphqlFilters = computed<TitleFilter>(() =>
	toGraphqlFilters(filtersNew.value, selectedProviders(!isLoggedIn.value), isHideSeenDislikedTitlesEnabled.value)
);

// We need this mapper due to filter naming on the New Page
// We have a TV Show filter on New Page that needs to fetch only TV Show Seasons items
const filterObjectTypes = computed(() =>
	graphqlFilters.value?.objectTypes?.map(objectType =>
		objectType === ObjectType.Show ? ObjectType.ShowSeason : objectType
	)
);

const queryVariables = computed<GetNewTitlesQueryVariables>(() => {
	const variables: GetNewTitlesQueryVariables = {
		date: props.timelineDate,
		filter: {
			...graphqlFilters.value,
			objectTypes: filterObjectTypes.value,
			packages: [props.providerShortName],
		},
		language: language.value,
		country: country.value,
		priceDrops: isPriceDropsTimeline.value,
		platform: Platform.Web,
		pageType: newPageType.value,
		showDateBadge:
			!!props.bucketType &&
			[NewDateRangeBucket.Next_7Days, NewDateRangeBucket.Next_30Days].includes(props.bucketType),
		availableToPackages: [props.providerShortName],
	};

	if ([NewPageType.LeavingSoon, NewPageType.Upcoming].includes(newPageType.value) && props.bucketType) {
		variables.bucketType = props.bucketType;
	}

	return variables;
});

const { result, loading, onResult, onError, fetchMore } = useQuery<GetNewTitlesQuery, GetNewTitlesQueryVariables>(
	GetNewTitlesDocument,
	queryVariables,
	() => ({
		fetchPolicy: 'cache-and-network',
		nextFetchPolicy: 'cache-first',
		enabled: !!newPageType.value,
	})
);

if (result.value) {
	isFirstPageLoaded.value = true;
}

onResult(() => {
	isFirstPageLoaded.value = true;
});

onError(() => {
	if (import.meta.client) {
		toastError({ message: t('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
	}
});

const runtimeConfig = useRuntimeConfig().public;

const providerTitlesData = computed(() => result.value?.newTitles);
const lazyLoadImages = computed(
	() => !!props.dayIndex || (props.providerIndex && props.providerIndex >= runtimeConfig.imgNewTimelineRows)
);

const imagesLoadCount = computed(() =>
	!props.dayIndex && props.providerIndex && props.providerIndex < runtimeConfig.imgNewTimelineRows
		? runtimeConfig.imgPosterLoadCount
		: 0
);

const providerTitles = computed(() =>
	(providerTitlesData.value?.edges ?? []).map(e => ({ ...e.node, watchNowOffer: e.watchNowOffer }))
);

const titleCountLabel = computed(() =>
	props.titleCount === 1
		? t('WEBAPP_TITLE', { NUMBER: props.titleCount })
		: t('WEBAPP_TITLES', { NUMBER: props.titleCount })
);

watch(filtersNew, () => {
	isFirstPageLoaded.value = false;
});

watch(providerTitlesData, () => {
	const edges = providerTitlesData.value?.edges || [];
	if (!!edges.length) {
		emit('passUpTitles', edges);
	}
});

function onPosterClick($event: { index: number; title: any }) {
	emit('onPosterClick', { ...$event, providerId: props.providerId, date: props.timelineDate });
}

async function onLoadNext(complete: Function) {
	if (!complete) return;

	try {
		await fetchMore({
			variables: {
				after: providerTitlesData.value?.pageInfo?.endCursor,
			},
			updateQuery(previousResult, { fetchMoreResult }) {
				if (!fetchMoreResult) return previousResult;

				return {
					newTitles: {
						__typename: previousResult.newTitles.__typename,
						totalCount: fetchMoreResult.newTitles.totalCount,
						edges: [...(previousResult.newTitles.edges || []), ...(fetchMoreResult.newTitles.edges || [])],
						pageInfo: fetchMoreResult.newTitles.pageInfo,
					},
				};
			},
		});
	} catch (error: any) {
		captureMessageForSentry(
			'[GraphQL New Page Load More Bucket Titles Issue]:',
			{ error, where: 'Component: ProviderTimeline, method: onLoadNext' },
			'error'
		);
	} finally {
		complete();
	}
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins.scss';

.provider-timeline {
	&__logo {
		position: absolute;

		$size: 24px;
		@include inset-inline('start', -$size + 10);

		margin-top: -2px;
		border-radius: 20%;
	}

	&__titles {
		margin-inline-start: 35px;
	}
}
</style>
